import { IconChevronDownSmall } from 'central-icons/IconChevronDownSmall';
import { IconCrossLarge } from 'central-icons/IconCrossLarge';
import classNames from 'classnames';
import {
  type ButtonHTMLAttributes,
  cloneElement,
  createContext,
  type HTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';

import styles from './RailModal.module.css';

interface AsChildProps {
  asChild?: boolean;
}

function mergeOntoChild(
  children: ReactNode,
  className: string
): ReactElement | null {
  if (!isValidElement(children)) return null;
  const child = children as ReactElement<{ className?: string }>;
  return cloneElement(child, {
    className: classNames(className, child.props.className),
  });
}

export const RailModalContent = ({
  asChild,
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement> & AsChildProps) => {
  const mergedClassName = classNames(styles.content, className);
  if (asChild) return mergeOntoChild(children, mergedClassName);
  return (
    <div className={mergedClassName} {...rest}>
      {children}
    </div>
  );
};

export const RailModalHeader = ({
  asChild,
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLElement> & AsChildProps) => {
  const mergedClassName = classNames(styles.header, className);
  if (asChild) return mergeOntoChild(children, mergedClassName);
  return (
    <header className={mergedClassName} {...rest}>
      {children}
    </header>
  );
};

export const RailModalCloseButton = ({
  asChild,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & AsChildProps) => {
  if (asChild) {
    return mergeOntoChild(children, classNames(styles.closeButton, className));
  }
  return (
    <button
      type="button"
      aria-label="Close"
      className={classNames(
        styles.closeButton,
        styles.closeButtonStandalone,
        className
      )}
      {...rest}
    >
      <IconCrossLarge size={16} />
    </button>
  );
};

export const RailModalDivider = ({
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={classNames(styles.divider, className)} {...rest} />
);

export const RailModalBody = ({
  asChild,
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement> & AsChildProps) => {
  const mergedClassName = classNames(styles.body, className);
  if (asChild) return mergeOntoChild(children, mergedClassName);
  return (
    <div className={mergedClassName} {...rest}>
      {children}
    </div>
  );
};

export const RailModalRail = ({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLElement>) => (
  <nav className={classNames(styles.rail, className)} {...rest}>
    {children}
  </nav>
);

interface RailModalRailGroupContextValue {
  onItemActivated: () => void;
}

const RailModalRailGroupContext =
  createContext<RailModalRailGroupContextValue | null>(null);

interface RailModalRailGroupProps extends HTMLAttributes<HTMLDivElement> {
  label: ReactNode;
  defaultCollapsed?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  groupId?: string;
}

export const RailModalRailGroup = ({
  label,
  defaultCollapsed = false,
  collapsed,
  onCollapsedChange,
  groupId,
  className,
  children,
  ...rest
}: RailModalRailGroupProps) => {
  const [uncontrolledCollapsed, setUncontrolledCollapsed] =
    useState(defaultCollapsed);
  const isCollapsed = collapsed ?? uncontrolledCollapsed;

  const isControlled = collapsed !== undefined;
  const setCollapsed = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setUncontrolledCollapsed(next);
      }
      onCollapsedChange?.(next);
    },
    [isControlled, onCollapsedChange]
  );

  const contextValue = useMemo<RailModalRailGroupContextValue>(
    () => ({ onItemActivated: () => setCollapsed(false) }),
    [setCollapsed]
  );

  const generatedId = useId();
  const contentId = groupId ?? generatedId;

  return (
    <div className={classNames(styles.group, className)} {...rest}>
      <button
        type="button"
        className={styles.groupHeader}
        aria-expanded={!isCollapsed}
        aria-controls={contentId}
        onClick={() => setCollapsed(!isCollapsed)}
      >
        <span>{label}</span>
        <span
          className={classNames(
            styles.chevron,
            isCollapsed && styles.chevronCollapsed
          )}
        >
          <IconChevronDownSmall size={28} />
        </span>
      </button>
      <div id={contentId} hidden={isCollapsed}>
        <RailModalRailGroupContext.Provider value={contextValue}>
          {children}
        </RailModalRailGroupContext.Provider>
      </div>
    </div>
  );
};

interface RailModalRailItemProps
  extends ButtonHTMLAttributes<HTMLButtonElement> {
  isActive?: boolean;
  trailing?: ReactNode;
}

export const RailModalRailItem = ({
  isActive = false,
  trailing,
  className,
  children,
  ...rest
}: RailModalRailItemProps) => {
  const group = useContext(RailModalRailGroupContext);
  const onItemActivated = group?.onItemActivated;

  useEffect(() => {
    if (isActive) {
      onItemActivated?.();
    }
  }, [isActive, onItemActivated]);

  return (
    <button
      type="button"
      className={classNames(
        styles.railItem,
        isActive && styles.railItemActive,
        className
      )}
      {...rest}
    >
      <span className={styles.railItemLabel}>{children}</span>
      {trailing && <span className={styles.railItemTrailing}>{trailing}</span>}
    </button>
  );
};

export const RailModalPane = ({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={classNames(styles.pane, className)} {...rest}>
    {children}
  </div>
);

export const RailModalFooter = ({
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDivElement>) => (
  <div className={classNames(styles.footer, className)} {...rest}>
    {children}
  </div>
);

export const RailModalFooterSpacer = ({
  className,
  ...rest
}: HTMLAttributes<HTMLSpanElement>) => (
  <span className={classNames(styles.footerSpacer, className)} {...rest} />
);
